import { Button } from "@mixitone/components";
import { ApplicationView } from "@mixitone/mvc";
import { decodeJwt } from "@mixitone/util/supabase";
import type { MouseEvent } from "react";
import { Layout } from "../Components/Layout";
import { PortalController } from "../controllers/PortalController";
import { injectParams } from "../lib/injectParams";
import { isTrustedUserGesture } from "../lib/isTrustedUserGesture";
import { createSupabase } from "../supabaseClient";

/**
 * Club invite links from email. Same click-to-continue guard as magic links:
 * scanners that only load the URL must not create a session.
 */

interface State {
  loading: boolean;
  expired: boolean;
  success: boolean;
  token: string;
}
interface Props {
  params: { token: string };
}

class MemberInviteLandingPageController extends PortalController<State, Props> {
  get initialState(): State {
    return {
      loading: false,
      expired: false,
      success: false,
      token: "",
    };
  }

  async initialize(props: Props) {
    this.state.token = props.params.token;
    try {
      const jwt = decodeJwt(props.params.token);
      if (typeof jwt.exp === "number" && jwt.exp * 1000 < Date.now()) {
        this.state.expired = true;
      }
    } catch {
      this.state.expired = true;
    }
  }

  async actionContinue() {
    if (this.state.loading || this.state.expired || this.state.success) return;

    this.state.loading = true;

    const supabase = createSupabase();
    const response = await supabase.functions.invoke("player-portal", {
      body: JSON.stringify({
        action: "swapNotificationJwt",
        token: this.state.token,
        page: typeof window !== "undefined" ? window.location.href : undefined,
      }),
    });
    if (response.error) {
      console.error(response.error);
      this.state.expired = true;
    } else if (!response.data?.access_token) {
      console.error("No access token found in response");
      this.state.expired = true;
    } else {
      const { access_token, refresh_token, club_id } = response.data;

      const session = await supabase.auth.setSession({
        access_token,
        refresh_token,
      });
      if (session.error) {
        console.error(session.error);
        this.state.expired = true;
      } else {
        this.state.success = true;
        this.actionSuccess(club_id);
      }
    }

    this.state.loading = false;
  }

  actionSuccess(clubId: string) {
    this.navigate(`/in/club/${clubId}/invite`);
  }
}

const ControlledMemberInviteLandingPage = () => {
  const controller = MemberInviteLandingPageController.use();
  const { loading, expired } = controller.state;

  const handleContinue = (event: MouseEvent<HTMLButtonElement>) => {
    if (!isTrustedUserGesture(event)) return;
    controller.actionContinue();
  };

  if (expired) {
    return (
      <Layout title="Member invite">
        <p>Your invite link has expired. Please contact your club administrator for a new one.</p>
      </Layout>
    );
  }

  return (
    <Layout title="Member invite">
      <p className="mb-4 text-sm text-gray-500">Continue to open this club invite.</p>
      <Button type="button" className="w-full" onClick={handleContinue} disabled={loading} spinner={loading}>
        Continue
      </Button>
    </Layout>
  );
};

export const MemberInviteLandingPage = injectParams(
  MemberInviteLandingPageController.scope(ApplicationView(ControlledMemberInviteLandingPage)),
);
